import { Component, OnInit, Inject } from '@angular/core';
import { Subscription, Observable } from 'rxjs';

import { LoginService } from 'src/app/services/login.service';
import { LoadingService } from 'src/app/services/loading.service';

import { StructuresService } from 'src/app/services/structures.service';
import { GroupsService } from 'src/app/services/groups.service';
import { DialogService } from 'src/app/services/dialog.service';

import { Group } from 'src/app/structures/group';
import { Structure } from 'src/app/structures/structure';

@Component({
    selector: 'app-groups',
    templateUrl: './groups.component.html',
    styleUrls: ['./groups.component.scss'],
    standalone: false
})
export class GroupsComponent implements OnInit {
    constructor(
        private loginService: LoginService,
        private loadingService: LoadingService,
        private structuresService: StructuresService,
        private groupsService: GroupsService,
        private dialogService: DialogService
    ) {}

    subscriptions$: Subscription = new Subscription();

    groups: Array<Group> = [];
    groupsCount = 0;
    groupTypeModalOpened = false;

    structures: Array<Structure>;
    usersTypes: Array<{
        key: string;
        title: string;
        selected: boolean;
    }> = [
        {
            title: 'Apprenants',
            key: 'learners',
            selected: true
        },
        {
            title: 'Formateurs',
            key: 'teachers',
            selected: false
        }
    ];
    groupStatus: Array<{
        key: string;
        title: string;
        selected: boolean;
    }> = [
        {
            title: 'Actif',
            key: 'active',
            selected: true
        },
        {
            title: 'Inactif',
            key: 'inactive',
            selected: false
        }
    ];
    searchTerm = '';
    searchPlaceholder = 'Chercher...';
    limit = 30;
    offset = 0;

    ngOnInit() {
        this.subscriptions$.add(
            this.structuresService.getStructures().subscribe((data: Structure[]) => {
                this.structures = data.map((structure: Structure) => ({
                    ...structure,
                    key: structure.id,
                    title: structure.name,
                    selected: this.loginService.getUser().structureid === structure.id
                }));
                if (!this.loginService.getUser().roles.nationalAdmin) {
                    this.onFiltersChange();
                }
            })
        );
    }

    onFiltersChange(): void {
        if (!this.structureSelected()) {
            return;
        }
        this.groups = [];
        this.offset = 0;
        this.getGroups();
        this.getGroupsCount();
    }

    /**
     * Build the filter parameters shared by the list and the count requests
     *
     * @description Both requests must apply the exact same filters, otherwise the
     * "N groupes" counter no longer matches the displayed list.
     * @returns {any} Filter parameters to merge into the request parameters
     */
    getFilterParams(): any {
        let params: any = {};
        if (this.searchTerm.length > 0) {
            params = {
                ...params,
                search: this.searchTerm
            };
        }
        if (this.userTypeSelected()) {
            params = {
                ...params,
                type: this.userTypeSelected() === 'teachers' ? 'teacher' : 'learner'
            };
        }
        if (this.groupStatusSelected()) {
            params = {
                ...params,
                disabled: this.groupStatusSelected() === 'inactive'
            };
        } else {
            // The API hides deactivated groups unless a caller opts in, so an empty
            // state filter has to ask for them explicitly to list both.
            params = {
                ...params,
                include_disabled: true
            };
        }
        return params;
    }

    getGroups(): void {
        const params: any = {
            structureid: this.structureSelected(),
            limit: this.limit,
            offset: this.offset,
            ...this.getFilterParams()
        };
        if (this.offset === 0) {
            this.loadingService.startLoading('groupsMainView', 'getGroups');
        } else {
            this.loadingService.startLoading('groupsNextPage', 'getGroups');
        }
        this.subscriptions$.add(
            this.groupsService.getGroups(params).subscribe((groupsReceived: Group[]) => {
                if (this.offset === 0) {
                    this.loadingService.stopLoading('groupsMainView', 'getGroups');
                } else {
                    this.loadingService.stopLoading('groupsNextPage', 'getGroups');
                }
                this.groups = [...this.groups, ...groupsReceived];
                this.offset += this.limit;
            })
        );
    }

    getGroupsCount(): void {
        const params: any = {
            structureid: this.structureSelected(),
            ...this.getFilterParams()
        };
        this.loadingService.startLoading('groupsMainView', 'getGroupsCount');

        this.subscriptions$.add(
            this.groupsService.getGroupsCount(params).subscribe((data: { count: number }) => {
                this.loadingService.stopLoading('groupsMainView', 'getGroupsCount');
                this.groupsCount = data.count;
            })
        );
    }

    isNationalAdmin(): boolean {
        if (this.loginService.getUser()) {
            return this.loginService.getUser().roles.nationalAdmin;
        }
    }

    userTypeSelected(): string | false {
        if (this.usersTypes.find((item) => item.selected)) {
            return this.usersTypes.find((item) => item.selected).key;
        }
        return false;
    }

    /**
     * Return the selected group state, or false when the filter is cleared
     *
     * @description The dropdown is single-select: clicking the selected entry unselects
     * it, which clears the filter and lists both active and inactive groups.
     * @returns {string | false} 'active', 'inactive', or false when no state is selected
     */
    groupStatusSelected(): string | false {
        if (this.groupStatus.find((item) => item.selected)) {
            return this.groupStatus.find((item) => item.selected).key;
        }
        return false;
    }

    structureSelected(): number | undefined {
        if (this.structures && this.structures.find((i) => i.selected)) {
            return this.structures.find((item) => item.selected).key;
        }
        return undefined;
    }

    openGroupTypeModal($event: Event): void {
        $event.stopImmediatePropagation();
        this.groupTypeModalOpened = true;
    }

    openCreateGroup(groupType: 'student' | 'teacher'): void {
        this.groupTypeModalOpened = false;
        if (!this.structureSelected()) {
            return;
        }
        const params = {
            localStructure: this.structures.find((s) => s.selected).name,
            structureid: this.structures.find((s) => s.selected).id,
            type: groupType === 'teacher' ? 'teacher' : 'learner',
            users: []
        };
        this.dialogService.openCreateGroup(params).subscribe({
            next: (groupCreated: Group | false) => {
                if (groupCreated) {
                    this.groups = [groupCreated, ...this.groups].sort((a, b) =>
                        a.name.localeCompare(b.name)
                    );
                    this.groupsCount += 1;
                }
            }
        });
    }

    isLoading(view: string) {
        return this.loadingService.isLoading(view);
    }
}
